import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { withMobileDialog } from '@material-ui/core';

import {
  getDashboards,
  addDashboard,
  editDashboard,
  deleteDashboard,
} from '@pingum/app-state-management/dashboard/actions';
import { getStatuses } from '@pingum/app-state-management/status/actions';
import { getTags } from '@pingum/app-state-management/tag/actions';
import { getEventData } from '@pingum/app-state-management/eventData/actions';
import { getTagTypes } from '@pingum/app-state-management/tagType/actions';
import { UserService } from '@pingum/api-services';

import DashboardsCard from './components/DashboardsCard';
import DeleteDashboardDialog from '../DashboardPage/dialogs/DeleteDashboardDialog';
import Page from '../../widgets/Page';

import { getTenantFromUrl } from '../../../utils/urlHelper';

const isUserAdmin = (roles) => roles.indexOf('admin') > -1 || roles.indexOf('owner') > -1;

export class DashboardSettingsComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openConfirmDeleteDashboardDialog: false,
      deleteDashboardId: null,
    };

    this.loadData();
  }

  loadData = () => {
    this.props.getDashboards();
    if (this.props.statuses.length === 0) {
      this.props.getStatuses();
    }
    if (this.props.tagTypes.length === 0) {
      this.props.getTagTypes();
    }
    if (this.props.tags.length === 0) {
      this.props.getTags();
    }
    if (this.props.eventData.length === 0) {
      this.props.getEventData();
    }
  }

  handleSave = (dashboardToSave) => {
    const dashboard = dashboardToSave;
    const roles = UserService.getUserRolesForTenant(
      this.props.profile,
      getTenantFromUrl(),
    );
    const isAdmin = isUserAdmin(roles);
    delete dashboard.dashboardData.tagNames;
    delete dashboard.dashboardData.statusNames;
    delete dashboard.dashboardData.eventTemplateId;
    delete dashboard.dashboardData.pastDue;

    if (dashboard.id && dashboard.id >= 0) {
      this.props.editDashboard(
        dashboard.id,
        {
          dashboardData: dashboard.dashboardData,
          defaultView: dashboard.defaultView,
          organizationExposed: isAdmin
            ? dashboard.organizationExposed
            : null,
          directLinkName: dashboard.directLinkName,
          name: dashboard.name,
          selectedColumns: dashboard.selectedColumns || [],
        },
      );
    } else {
      this.props.addDashboard({
        dashboardData: dashboard.dashboardData,
        defaultView: dashboard.defaultView,
        organizationExposed: isAdmin
          ? dashboard.organizationExposed
          : null,
        directLinkName: dashboard.directLinkName,
        name: dashboard.name,
        selectedColumns: dashboard.selectedColumns || [],
      });
    }
  }

  handleOpenDelete = (deleteDashboardId) => {
    this.setState({
      openConfirmDeleteDashboardDialog: true,
      deleteDashboardId,
    });
  }

  handleCloseDelete = () => {
    this.setState({
      openConfirmDeleteDashboardDialog: false,
      deleteDashboardId: null,
    });
  }

  handleDeleteConfirm = () => {
    this.props.deleteDashboard(this.state.deleteDashboardId);
    this.setState({
      openConfirmDeleteDashboardDialog: false,
      deleteDashboardId: null,
    });
  }

  render() {
    const {
      hidePageContent,
      fullScreen,
      dashboards,
      profile,
      eventData,
      isLoadingDashboards,
    } = this.props;

    const roles = UserService.getUserRolesForTenant(
      profile,
      getTenantFromUrl(),
    );
    const isAdmin = isUserAdmin(roles);

    const content = (
      <div>
        <DashboardsCard
          fullScreen={fullScreen}
          dashboards={dashboards}
          isAdmin={isAdmin}
          userEmail={profile.email}
          onSave={this.handleSave}
          onDelete={this.handleOpenDelete}
          isLoadingDashboards={isLoadingDashboards}
          eventData={eventData}
        />

        <DeleteDashboardDialog
          open={this.state.openConfirmDeleteDashboardDialog}
          onClose={this.handleCloseDelete}
          onConfirm={this.handleDeleteConfirm}
          onCancel={this.handleCloseDelete}
        />
      </div>
    );

    return (
      !hidePageContent
        ? (
          <Page title="My Dashboards">
            {content}
          </Page>
        )
        : content
    );
  }
}

DashboardSettingsComponent.propTypes = {
  hidePageContent: PropTypes.bool,
  profile: PropTypes.object,
  fullScreen: PropTypes.bool,
  dashboards: PropTypes.array,
  statuses: PropTypes.array,
  tags: PropTypes.array,
  tagTypes: PropTypes.array,
  eventData: PropTypes.array,
  getDashboards: PropTypes.func,
  getStatuses: PropTypes.func,
  getTags: PropTypes.func,
  getTagTypes: PropTypes.func,
  addDashboard: PropTypes.func,
  editDashboard: PropTypes.func,
  deleteDashboard: PropTypes.func,
  getEventData: PropTypes.func,
  isLoadingDashboards: PropTypes.bool,
};

DashboardSettingsComponent.defaultProps = {
  hidePageContent: false,
  profile: {},
  fullScreen: false,
  dashboards: [],
  statuses: [],
  tags: [],
  tagTypes: [],
  eventData: [],
  getDashboards: () => {},
  getStatuses: () => {},
  getEventData: () => {},
  getTags: () => {},
  getTagTypes: () => {},
  addDashboard: () => {},
  editDashboard: () => {},
  deleteDashboard: () => {},
  isLoadingDashboards: true,
};

export const mapStateToProps = (state) => ({
  profile: state.user.user,
  eventData: state.eventData.eventData,
  dashboards: state.dashboard.dashboards,
  statuses: state.status.statuses,
  tags: state.tag.tags,
  tagTypes: state.tagType.tagTypes,
  isLoadingDashboards: state.dashboard.isFetchingDashboards,
});

export const mapDispatchToProps = (dispatch) => ({
  getDashboards: () => dispatch(getDashboards()),
  getStatuses: () => dispatch(getStatuses(false)),
  getTags: () => dispatch(getTags(false, false)),
  getTagTypes: () => dispatch(getTagTypes(false, false)),
  getEventData: () => dispatch(getEventData(false)),
  addDashboard: (request) => dispatch(addDashboard(request)),
  editDashboard: (id, request) => dispatch(editDashboard(id, request)),
  deleteDashboard: (id) => dispatch(deleteDashboard(id)),
});

export const DashboardSettingsConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(DashboardSettingsComponent));
export default withRouter(DashboardSettingsConnected);
